import React, { useState, useEffect } from 'react';
import { Globe, Save } from 'lucide-react';
import { integrationService } from '../../services/integrationService';
import { getSaasApiBaseUrl } from '../../utils/saasApiBaseUrl';

const getSaaSApiBaseUrl = getSaasApiBaseUrl;

const CLEARINGHOUSE_OPTIONS = [
  { value: 'stedi', label: 'Stedi' },
  { value: 'availity', label: 'Availity' },
  { value: 'change_healthcare', label: 'Change Healthcare' },
  { value: 'optum', label: 'Optum' },
];

function formatApiError(payload) {
  const d = payload?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    return d.map((x) => (typeof x === 'object' && x?.msg ? x.msg : String(x))).join(' ');
  }
  return payload?.message || 'Request failed';
}

/**
 * Organization-level primary / backup clearinghouse (source of truth in SaaS settings.edi).
 * SaaS PATCH syncs to EdiPlatform clearinghouse-defaults server-side.
 */
export default function EdiClearinghouseOrganizationSettingSection({
  organization,
  onMessage,
  onSaved,
}) {
  const organizationId = organization?.id || organization?.organization_id;
  const [primary, setPrimary] = useState('stedi');
  const [secondary, setSecondary] = useState('');
  const [saving, setSaving] = useState(false);
  const [chOptions, setChOptions] = useState(CLEARINGHOUSE_OPTIONS);

  useEffect(() => {
    const edi = organization?.settings?.edi;
    if (edi) {
      setPrimary(edi.primary_clearinghouse_key || 'stedi');
      setSecondary(edi.secondary_clearinghouse_key || '');
    }
  }, [organization]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await integrationService.fetchEdiClearinghouses();
        if (cancelled || !Array.isArray(list) || list.length === 0) return;
        const opts = list.map((ch) => {
          const key = (ch.name || ch.key || '').toLowerCase().replace(/\s+/g, '_');
          const label = ch.name || ch.key || key;
          return { value: key, label };
        });
        const seen = new Set();
        const merged = [...CLEARINGHOUSE_OPTIONS];
        opts.forEach((o) => {
          if (o.value && !seen.has(o.value)) {
            seen.add(o.value);
            if (!merged.find((m) => m.value === o.value)) merged.push(o);
          }
        });
        setChOptions(merged);
      } catch {
        /* keep static options */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!organizationId) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const API_BASE_URL = getSaaSApiBaseUrl();
      const res = await fetch(`${API_BASE_URL}/organizations/${organizationId}/settings`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          edi: {
            primary_clearinghouse_key: primary,
            secondary_clearinghouse_key: secondary || null,
          },
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(formatApiError(err) || `Request failed (${res.status})`);
      }
      onMessage?.({
        type: 'success',
        text:
          'Clearinghouse routing saved for this organization and synced to EDI Platform.',
      });
      onSaved?.();
    } catch (err) {
      onMessage?.({
        type: 'error',
        text: err.message || 'Failed to save clearinghouse settings.',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    if (!organizationId) return;
    if (!window.confirm('Clear organization clearinghouse defaults? Global payer routing will apply.')) {
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const API_BASE_URL = getSaaSApiBaseUrl();
      const res = await fetch(`${API_BASE_URL}/organizations/${organizationId}/settings`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          edi: {
            primary_clearinghouse_key: null,
            secondary_clearinghouse_key: null,
          },
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(formatApiError(err) || `Request failed (${res.status})`);
      }
      setPrimary('stedi');
      setSecondary('');
      onMessage?.({ type: 'success', text: 'Organization clearinghouse defaults cleared.' });
      onSaved?.();
    } catch (err) {
      onMessage?.({ type: 'error', text: err.message || 'Failed to clear settings.' });
    } finally {
      setSaving(false);
    }
  };

  if (!organizationId) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-600">
        Sign in and load your organization to configure clearinghouse routing.
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
      <div className="flex items-center gap-2 text-gray-900 font-medium">
        <Globe className="w-5 h-5 text-emerald-600" aria-hidden />
        Clearinghouse routing (organization)
      </div>
      <p className="text-sm text-gray-600">
        Primary and backup clearinghouse for this practice. Saved in organization settings and
        applied automatically on the EDI Platform for eligibility, claims, and remittance traffic.
        API keys and SFTP credentials (Stedi, Trizetto) are configured in{' '}
        <strong>EDI Platform → Settings → Stedi</strong> (or Trizetto).
      </p>
      <form onSubmit={handleSave} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Primary clearinghouse
            </label>
            <select
              value={primary}
              onChange={(e) => setPrimary(e.target.value)}
              disabled={saving}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              {chOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Backup clearinghouse (optional)
            </label>
            <select
              value={secondary}
              onChange={(e) => setSecondary(e.target.value)}
              disabled={saving}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="">None</option>
              {chOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-md hover:bg-emerald-700 disabled:opacity-50"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving…' : 'Save clearinghouse routing'}
          </button>
          <button
            type="button"
            onClick={handleClear}
            disabled={saving}
            className="px-4 py-2 border border-gray-300 text-sm text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Clear defaults
          </button>
        </div>
      </form>
    </div>
  );
}
